import { RadioGroup } from '@headlessui/react';
import { cn } from '../../../utils';
import ErrorOrHelp from '../ErrorOrHelp';
import { ForwardedRef, forwardRef } from 'react';

export type RadioCardOptionProps = {
    id: string;
    value: string;
    disabled?: boolean;
};

export type OrientationProp = {
    orientation?: 'vertical' | 'horizontal';
};

export type RadioSmallCardsProps = {
    name: string;
    title?: RadioTitleProps;
    screenReaderLabel?: string;
    onChange?: (value: string) => void;
    error?: string;
    value?: string;
    options: RadioCardOptionProps[];
} & OrientationProp;

export default forwardRef(function RadioSmallCards(
    {
        title,
        screenReaderLabel,
        onChange,
        orientation = 'vertical',
        options,
        error,
        name,
        value,
        ...rest
    }: RadioSmallCardsProps,
    ref: ForwardedRef<HTMLInputElement>,
) {
    return (
        <div {...rest}>
            {title && (
                <RadioTitle
                    title={title.title}
                    link={title?.link}
                    orientation={orientation}
                />
            )}
            <RadioGroup value={value} onChange={onChange} className="alli-mt-2">
                {screenReaderLabel && (
                    <RadioGroup.Label className="alli-sr-only">
                        {screenReaderLabel}
                    </RadioGroup.Label>
                )}

                <div
                    className={cn('alli-flex alli-gap-5', {
                        'alli-flex-col': orientation === 'vertical',
                        'alli-flex-wrap': orientation !== 'vertical',
                    })}>
                    {options.map(option => (
                        <RadioGroup.Option
                            {...ref}
                            key={option.value}
                            value={option.value}
                            data-testid={option.value}
                            className={({ active, checked, disabled }) =>
                                cn(
                                    'alli-flex alli-w-36 alli-flex-shrink-0 alli-cursor-pointer alli-items-center alli-justify-center alli-rounded-md alli-bg-white alli-px-3 alli-py-3 alli-text-sm alli-font-medium alli-uppercase alli-text-blue-gray-700 focus:alli-outline-none',
                                    {
                                        'alli-cursor-not-allowed alli-border alli-border-gray-500 alli-opacity-50':
                                            disabled,
                                        'alli-border alli-border-blue-600 alli-text-blue-gray-700 hover:alli-bg-blue-gray-50':
                                            checked,
                                        'alli-border alli-border-gray-500 alli-ring-2 alli-ring-transparent hover:alli-bg-blue-gray-50':
                                            !checked && !disabled,
                                        'alli-border-blue-600 alli-ring-1 alli-ring-blue-600 focus:alli-ring-1 focus:alli-ring-blue-600 ':
                                            active,
                                        'alli-border-red-400':
                                            error && !disabled,
                                    },
                                )
                            }
                            disabled={option.disabled}>
                            <RadioGroup.Label as="span">
                                {option.value}
                            </RadioGroup.Label>
                        </RadioGroup.Option>
                    ))}
                </div>
            </RadioGroup>
            {error && <ErrorOrHelp error={error} name={name} />}
        </div>
    );
});

type RadioTitleProps = {
    title: string;
    link?: {
        href: string;
        content: string;
    };
} & OrientationProp;

export function RadioTitle({ title, link, orientation }: RadioTitleProps) {
    return (
        <div
            className={cn('alli-items-center alli-justify-between', {
                'alli-block': orientation === 'vertical',
                'alli-flex': orientation === 'horizontal',
            })}>
            <h2 className="alli-text-sm alli-font-medium alli-leading-6 alli-text-gray-900">
                {title}
            </h2>
            {link && (
                <a
                    href={link.href}
                    className="alli-text-sm alli-font-medium alli-leading-6 alli-text-blue-600 hover:alli-text-blue-500">
                    {link.content}
                </a>
            )}
        </div>
    );
}
